import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import type { NIBSubmission } from '@/types';
import { NIB_FORMAT_LABELS } from '@/types';

interface SubmissionSummaryProps {
  submission: NIBSubmission;
  isLoading?: boolean;
}

interface StatCard {
  title: string;
  value: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description?: string;
}

export function SubmissionSummary({
  submission,
  isLoading = false,
}: SubmissionSummaryProps) {
  const formatCurrency = (amount: number | string): string => {
    const numValue = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `BSD ${numValue.toFixed(2)}`;
  };

  const stats: StatCard[] = [
    {
      title: 'Total Records',
      value: String(submission.total_records || 0),
      icon: UserGroupIcon,
      description: 'Employee records in submission',
    },
    {
      title: 'Total Insurable Wages',
      value: formatCurrency(submission.total_insurable_wages),
      icon: CurrencyDollarIcon,
      description: 'After truncation and capping',
    },
    {
      title: 'Employee Contributions',
      value: formatCurrency(submission.total_employee_contributions),
      icon: ReceiptPercentIcon,
      description: 'Based on employee category rates',
    },
    {
      title: 'Employer Contributions',
      value: formatCurrency(submission.total_employer_contributions),
      icon: BuildingOffice2Icon,
      description: 'Based on employer category rates',
    },
  ];

  // Add gratuity contribution if hospitality format
  if (submission.format_type === 'HOSPITALITY') {
    stats.push({
      title: 'Gratuity Contributions',
      value: formatCurrency(submission.total_gratuity_contributions),
      icon: BanknotesIcon,
      description: '9.8% of gratuity wages',
    });
  }

  // Always add total contributions last
  stats.push({
    title: 'Total Contributions',
    value: formatCurrency(submission.total_contributions),
    icon: DocumentTextIcon,
    description: 'Sum of all contributions',
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-gray-200 rounded-md"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900">
          Submission Summary
        </h2>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {NIB_FORMAT_LABELS[submission.format_type]}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                  <stat.icon className="h-6 w-6" aria-hidden="true" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.title}
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
            {stat.description && (
              <div className="mt-4">
                <div className="text-sm text-gray-600">{stat.description}</div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
